import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Formik } from "formik";
import * as Yup from "yup";

import { updateCustomer } from "src/api/customer";
import { TextField } from "src/components/form";
import { Button } from "src/components/ui/button";
import { usePortalContext } from "src/context/PortalContext";
import { useAuth } from "src/providers/AuthProvider";
import { getCustomizationValue, showSuccessToast } from "src/lib/utils";
import type { CustomerContactDetails } from "src/types/customer";

const validationSchema = Yup.object().shape({
	first_name: Yup.string().required("This field is required"),
	last_name: Yup.string().required("This field is required"),
	phone: Yup.string().required("This field is required"),
	email: Yup.string().email("Enter a valid email"),
	address: Yup.string().required("This field is required"),
	business_name: Yup.string().required("This field is required"),
});

interface EditCustomerFormValues {
	uuid: string;
	first_name: string;
	last_name: string;
	phone: string;
	email: string;
	address: string;
	business_name: string;
}

interface EditCustomerModalProps {
	customer: CustomerContactDetails;
}

const EditCustomerModal = ({ customer }: EditCustomerModalProps) => {
	const { user } = useAuth();
	const queryClient = useQueryClient();
	const closePortal = usePortalContext();

	const customerLabel = getCustomizationValue(user, "customer_label", true) || "Customer";

	const initialValues: EditCustomerFormValues = {
		uuid: customer.uuid || "",
		first_name: customer.first_name || "",
		last_name: customer.last_name || "",
		phone: customer.phone || "",
		email: customer.email || "",
		address: customer.address || "",
		business_name: customer.business_name || "",
	};

	const { mutate, isLoading } = useMutation(updateCustomer, {
		onSuccess: ({ message }) => {
			showSuccessToast(message ?? "");
			queryClient.invalidateQueries(["customers", customer?.uuid]);
			closePortal();
		},
	});

	return (
		<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={(values) => mutate(values)}>
			{({ values, handleChange, handleSubmit, errors, touched }) => (
				<form onSubmit={handleSubmit} className="tw-grid tw-gap-4">
					<div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
						<TextField
							label="First name"
							name="first_name"
							placeholder="First name"
							value={values.first_name}
							error={touched.first_name && errors.first_name}
							onChange={handleChange}
							required
						/>
						<TextField
							label="Last name"
							name="last_name"
							placeholder="Last name"
							value={values.last_name}
							error={touched.last_name && errors.last_name}
							onChange={handleChange}
							required
						/>
						<TextField
							label="Phone"
							name="phone"
							placeholder="Phone number"
							value={values.phone}
							error={touched.phone && errors.phone}
							onChange={handleChange}
							required
						/>
						<TextField
							label="Email"
							name="email"
							type="email"
							placeholder="name@example.com"
							value={values.email}
							error={touched.email && errors.email}
							onChange={handleChange}
						/>
					</div>

					<TextField
						label="Business name"
						name="business_name"
						placeholder="Business name"
						value={values.business_name}
						error={touched.business_name && errors.business_name}
						onChange={handleChange}
						required
					/>

					<TextField
						label="Address"
						name="address"
						placeholder="Address"
						value={values.address}
						error={touched.address && errors.address}
						onChange={handleChange}
						required
					/>

					<Button type="submit" variant="secondary" className="tw-mt-1 tw-w-full" disabled={isLoading}>
						{isLoading ? "Updating..." : `Update ${customerLabel}`}
					</Button>
				</form>
			)}
		</Formik>
	);
};

export default EditCustomerModal;
